import { useEffect, useRef, useState } from 'react';
import type { MissionSnapshot } from '@shared/types.js';
import { extractMapsUrl } from '@shared/geo.js';
import { mapsHref, normalizeLinkForCompare } from '@shared/mediaUrl.js';
import { YouTubeEmbed } from './YouTubeEmbed.js';
import { LocationButton } from './LocationButton.js';
import { MissionMedia } from './MissionMedia.js';
import { gameTimeZone } from '../../lib/gameTime.js';
import { FormattedText } from './FormattedText.js';
import { unlockMethodEmoji, unlockMethodLabel } from '@shared/unlock.js';
import type { UnlockMethod } from '@shared/types.js';

/**
 * תוכן משימה — משמש הן להצגת המשימה הנוכחית והן לפריט History.
 * מקבל את תוכן הפתיחה כפי שהשרת מחזיר אותו, והשרת מעדכן אותו אוטומטית בכל שינוי במשימה
 * (בגיליון או בעורך המנהל) — גם אחרי שהיא כבר נפתחה, לבקשת בעל המשחק. כשהתוכן משתנה בזמן
 * שהשחקן צופה בו מופיע הבהוב קצר + תג "עודכן", כדי שהשינוי לא יעבור בלי שישימו לב.
 *
 * שדה "הכנות" (preparations) נשאר בגיליון ובעורך המנהל בלבד — לבקשת המשתמש הוא לא
 * מוצג לסוכן (לא במסך הסוכן וגם לא ב-History).
 */
export function MissionContent({
  snapshot,
  unlockMethod,
  openedAt,
}: {
  snapshot: MissionSnapshot;
  unlockMethod?: UnlockMethod;
  openedAt?: string;
}) {
  // אותו מיקום יכול להיכתב בכמה מקומות (בטקסט, בעמודת "תמונה", בעמודת "אפשור מיקום") — כפתור אחד לכל מיקום, לפי הסדר הזה
  const seenMaps = new Set<string>();
  const firstTime = (href: string | null): string | null => {
    if (!href) return null;
    const key = normalizeLinkForCompare(href);
    if (seenMaps.has(key)) return null;
    seenMaps.add(key);
    return href;
  };
  const taskMapsButton = firstTime(extractMapsUrl(snapshot.task));
  const imageMapsButton = firstTime(mapsHref(snapshot.imageUrl));
  const unlockLocation = snapshot.locationUrl?.trim() ?? '';
  const unlockMapsButton = unlockLocation ? firstTime(mapsHref(unlockLocation) ?? unlockLocation) : null;

  const contentKey = JSON.stringify(snapshot);
  const previousKey = useRef(contentKey);
  const [justUpdated, setJustUpdated] = useState(false);
  useEffect(() => {
    if (previousKey.current === contentKey) return;
    previousKey.current = contentKey;
    setJustUpdated(true);
    const t = window.setTimeout(() => setJustUpdated(false), 2_400);
    return () => window.clearTimeout(t);
  }, [contentKey]);

  return (
    <div className={justUpdated ? 'mission-content mission-content--updated' : 'mission-content'}>
      <div className="mission-content__meta mono">
        {justUpdated && <span className="mission-content__updated-tag">עודכן</span>}
        {snapshot.missionNumber !== null && <span>משימה #{snapshot.missionNumber}</span>}
        {unlockMethod && (
          <span>
            {unlockMethodEmoji(unlockMethod)} {unlockMethodLabel(unlockMethod)}
          </span>
        )}
      </div>

      <div className="mission-content__task term-text">
        <FormattedText text={snapshot.task} />
      </div>
      {taskMapsButton && <LocationButton url={taskMapsButton} />}

      {snapshot.youtubeUrl?.trim() && <YouTubeEmbed url={snapshot.youtubeUrl} />}

      {/* עמודת "תמונה": תמונה / מיקום במפות / סרטון / קישור — לפי מה שכתוב בה */}
      <MissionMedia url={snapshot.imageUrl ?? ''} hideMaps={imageMapsButton === null} />

      {unlockMapsButton && <LocationButton url={unlockMapsButton} />}

      {openedAt && (
        <div className="mission-content__opened-at mono">נפתחה: {formatOpenedAt(openedAt)}</div>
      )}
    </div>
  );
}

function formatOpenedAt(iso: string): string {
  try {
    return new Intl.DateTimeFormat('he-IL', {
      timeZone: gameTimeZone(),
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    }).format(new Date(iso));
  } catch {
    return iso;
  }
}
